import { useEffect, useState } from 'preact/hooks';
import type { LeafDto } from '../api/types';
import { api, ApiError } from '../api/client';
import { IconCancel, IconSave } from '../icons/HvacIcons';
import './LeafConfigModal.css';

interface Props {
  plantId: string;
  leaf: LeafDto;
  /** Called after a successful save with the server's response payload
   *  (which mirrors LeafDto shape for the editable fields). The caller
   *  patches its local schema so the DP-Viewer reflects the new values
   *  immediately, even before the SignalR event lands. */
  onSaved(patch: LeafPatch): void;
  /** Optional: when provided, exposes a soft-delete button in the footer.
   *  The caller is responsible for removing the leaf from any local schema
   *  copy after this fires. */
  onDeleted?(leafId: string): void;
  onClose(): void;
  /** All node candidates on the leaf's device, in tree-walk order with
   *  <c>depth</c> for indentation. Enables the "Verschieben nach…" selector.
   *  When omitted (or ≤ 1 entry), the section is hidden — moving needs
   *  somewhere to move to. */
  moveOptions?: MoveOption[];
  /** The leaf's current parent-node id — used as the initial value of the
   *  move-selector and to detect a change on save. */
  currentParentNodeId?: string;
  /** Fires after a successful parent change so the caller can force a
   *  full schema refetch (patching the local copy would be gnarly — the
   *  leaf moved between two subtrees, both need updating). */
  onMoved?(leafId: string, newParentNodeId: string): void;
}

export interface MoveOption {
  id: string;
  label: string;
  depth: number;
}

export interface LeafPatch {
  leafId: string;
  displayName: string | null;
  unit: string | null;
  dimension: string | null;
  minValue: number | null;
  maxValue: number | null;
  decimals: number | null;
  warnLow: number | null;
  warnHigh: number | null;
  alarmLow: number | null;
  alarmHigh: number | null;
  readSensitivity: string;
  writeSensitivity: string;
  safetyMarker: boolean;
  forceAllowed: boolean;
  escalateToAlarm: boolean;
  metricKind: string;
  // Acquisition-binding fields. Optional in the patch: undefined = the
  // caller didn't touch the binding endpoint this save (only schema
  // fields changed), so PlantSchemaPage should keep whatever it already
  // has for these keys.
  address?: string | null;
  scale?: number | null;
  offset?: number | null;
  pollIntervalSeconds?: number | null;
  heartbeatSeconds?: number | null;
  deadband?: number | null;
  covPercent?: number | null;
  minIntervalSeconds?: number | null;
}

interface DraftState {
  displayName: string;
  unit: string;
  dimension: string;
  minValue: string;
  maxValue: string;
  decimals: string;
  warnLow: string;
  warnHigh: string;
  alarmLow: string;
  alarmHigh: string;
  readSensitivity: string;
  writeSensitivity: string;
  safetyMarker: boolean;
  forceAllowed: boolean;
  escalateToAlarm: boolean;
  metricKind: string;
  // Acquisition binding — driver-facing polling config. Empty string ==
  // "clear this column on save"; anything else is parsed on submit.
  address: string;
  scale: string;
  offset: string;
  pollIntervalSeconds: string;
  heartbeatSeconds: string;
  deadband: string;
  covPercent: string;
  minIntervalSeconds: string;
}

const SENSITIVITY_OPTIONS = ['Normal', 'Elevated', 'Critical'];
const METRIC_KIND_OPTIONS = ['Unknown', 'Instantaneous', 'CumulativeCounter', 'Rate', 'State', 'Setpoint'];

/**
 * Modal to edit a leaf's schema metadata — the operator-facing surface of
 * the PATCH /api/plants/{plantId}/leaves/{leafId} endpoint. Non-editable
 * fields (Symbol, ValueType, Id) are shown as read-only context. The save
 * button is disabled while a request is in flight; server-side validation
 * errors surface inline.
 */
export function LeafConfigModal({
  plantId,
  leaf,
  onSaved,
  onDeleted,
  onClose,
  moveOptions,
  currentParentNodeId,
  onMoved,
}: Props) {
  const [draft, setDraft] = useState<DraftState>(() => initialDraft(leaf));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [targetParentId, setTargetParentId] = useState<string>(currentParentNodeId ?? '');
  const showMoveSelector = (moveOptions?.length ?? 0) >= 2 && !!currentParentNodeId;

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const set = <K extends keyof DraftState>(key: K, val: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: val }));
  };

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      // Two independent endpoints: display metadata (+ optional reparent)
      // vs acquisition binding. Dispatch each only if the operator actually
      // changed one of its fields. Fires in parallel; the two responses
      // merge into one patch that PlantSchemaPage applies to its local
      // schema copy.
      const schemaBody = buildRequestBody(draft, leaf);
      const bindingBody = buildBindingBody(draft, leaf);
      const parentChanged = showMoveSelector
        && targetParentId !== ''
        && targetParentId !== currentParentNodeId;
      if (parentChanged) {
        (schemaBody as Record<string, unknown>).parentNodeId = targetParentId;
      }
      const schemaChanged = Object.keys(schemaBody).length > 0;
      const bindingChanged = Object.keys(bindingBody).length > 0;

      if (!schemaChanged && !bindingChanged) {
        onClose();
        return;
      }

      const [schemaResp, bindingResp] = await Promise.all([
        schemaChanged
          ? api.patch<LeafPatch>(`/api/plants/${plantId}/leaves/${leaf.id}`, schemaBody)
          : Promise.resolve(null),
        bindingChanged
          ? api.patch<BindingResponse>(`/api/plants/${plantId}/leaves/${leaf.id}/binding`, bindingBody)
          : Promise.resolve(null),
      ]);

      // Merge: schema-response is the base LeafPatch shape; the binding
      // response overlays the six binding fields. When one endpoint wasn't
      // called we synthesize its part from the current leaf so PlantSchema
      // -Page's patcher gets a complete LeafPatch either way.
      const merged: LeafPatch = {
        ...(schemaResp ?? synthesizeSchemaPatch(leaf)),
        ...(bindingResp ?? {}),
      };
      onSaved(merged);
      // Reparent needs a full refetch to shift the leaf between subtrees —
      // patching in-place would leak a copy in the old parent. Delegate
      // that to the caller via onMoved.
      if (parentChanged) onMoved?.(leaf.id, targetParentId);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError('Keine Berechtigung. `plants.schema.edit` fehlt.');
        } else if (err.status === 404) {
          setError('Datenpunkt nicht mehr vorhanden.');
        } else if (err.status === 400) {
          setError(err.body && typeof err.body === 'object' && 'error' in err.body
            ? String((err.body as { error: unknown }).error)
            : 'Ungültige Eingabe.');
        } else {
          setError('Speichern fehlgeschlagen.');
        }
      } else {
        setError('Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="hv-leafcfg" role="dialog" aria-modal="true" aria-labelledby="hv-leafcfg-title">
      <div class="hv-leafcfg__backdrop" onClick={onClose} />
      <div class="hv-leafcfg__panel">
        <header class="hv-leafcfg__header">
          <div>
            <h2 id="hv-leafcfg-title" class="hv-leafcfg__title">
              Datenpunkt konfigurieren
            </h2>
            <p class="hv-leafcfg__subtitle muted">
              <span class="mono">{leaf.symbol}</span> — {leaf.valueType}
            </p>
          </div>
          <button
            type="button"
            class="hv-leafcfg__close"
            onClick={onClose}
            aria-label="Schließen"
            title="Schließen"
          >
            ×
          </button>
        </header>

        <div class="hv-leafcfg__body">
          {showMoveSelector && (
            <Section title="Position im Baum">
              <Field label="Verschieben nach"
                help="Ziel-Node muss unter demselben Device liegen — Cross-Device-Moves sind nicht erlaubt.">
                <select
                  class="hv-leafcfg__input"
                  value={targetParentId}
                  onChange={(e) => setTargetParentId((e.currentTarget as HTMLSelectElement).value)}
                >
                  {moveOptions!.map((o) => (
                    <option key={o.id} value={o.id}>
                      {'  '.repeat(o.depth) + o.label}
                    </option>
                  ))}
                </select>
              </Field>
            </Section>
          )}

          <Section title="Grundangaben">
            <Field label="Anzeigename">
              <input
                type="text"
                class="hv-leafcfg__input"
                value={draft.displayName}
                onInput={(e) => set('displayName', (e.currentTarget as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Einheit">
              <input
                type="text"
                class="hv-leafcfg__input"
                value={draft.unit}
                onInput={(e) => set('unit', (e.currentTarget as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Dimension">
              <input
                type="text"
                class="hv-leafcfg__input"
                value={draft.dimension}
                onInput={(e) => set('dimension', (e.currentTarget as HTMLInputElement).value)}
              />
            </Field>
            <Field label="Nachkommastellen">
              <input
                type="number"
                class="hv-leafcfg__input hv-leafcfg__input--num"
                min={0} max={15} step={1}
                value={draft.decimals}
                onInput={(e) => set('decimals', (e.currentTarget as HTMLInputElement).value)}
              />
            </Field>
          </Section>

          <Section title="Wertebereich">
            <Field label="Min">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                value={draft.minValue}
                onInput={(e) => set('minValue', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Max">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                value={draft.maxValue}
                onInput={(e) => set('maxValue', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
          </Section>

          <Section title="Betriebsgrenzen">
            <Field label="Alarm ↓">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                value={draft.alarmLow}
                onInput={(e) => set('alarmLow', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Warn ↓">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                value={draft.warnLow}
                onInput={(e) => set('warnLow', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Warn ↑">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                value={draft.warnHigh}
                onInput={(e) => set('warnHigh', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Alarm ↑">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                value={draft.alarmHigh}
                onInput={(e) => set('alarmHigh', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Als Alarm eskalieren" help="Nur wenn das Gerät den Alarm nicht selbst meldet.">
              <label class="hv-leafcfg__inline-check">
                <input type="checkbox"
                  checked={draft.escalateToAlarm}
                  onChange={(e) => set('escalateToAlarm', (e.currentTarget as HTMLInputElement).checked)} />
                <span class="muted">{draft.escalateToAlarm ? 'aktiv' : 'aus'}</span>
              </label>
            </Field>
          </Section>

          <Section title="Sicherheit">
            <Field label="Lese-Sensitivität">
              <select class="hv-leafcfg__input"
                value={draft.readSensitivity}
                onChange={(e) => set('readSensitivity', (e.currentTarget as HTMLSelectElement).value)}>
                {SENSITIVITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Schreib-Sensitivität">
              <select class="hv-leafcfg__input"
                value={draft.writeSensitivity}
                onChange={(e) => set('writeSensitivity', (e.currentTarget as HTMLSelectElement).value)}>
                {SENSITIVITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Safety-Marker">
              <label class="hv-leafcfg__inline-check">
                <input type="checkbox"
                  checked={draft.safetyMarker}
                  onChange={(e) => set('safetyMarker', (e.currentTarget as HTMLInputElement).checked)} />
                <span class="muted">{draft.safetyMarker ? 'gesetzt' : 'aus'}</span>
              </label>
            </Field>
            <Field label="Force erlaubt">
              <label class="hv-leafcfg__inline-check">
                <input type="checkbox"
                  checked={draft.forceAllowed}
                  onChange={(e) => set('forceAllowed', (e.currentTarget as HTMLInputElement).checked)} />
                <span class="muted">{draft.forceAllowed ? 'ja' : 'nein'}</span>
              </label>
            </Field>
          </Section>

          <Section title="Semantik">
            <Field label="MetricKind">
              <select class="hv-leafcfg__input"
                value={draft.metricKind}
                onChange={(e) => set('metricKind', (e.currentTarget as HTMLSelectElement).value)}>
                {METRIC_KIND_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </Section>

          <Section title="Akquisition (Feld-Binding)">
            <Field label="Adresse" help="Treiber-spezifisch: z. B. h:5222 (Modbus Holding), c:53 (Coil), oder ein BACnet-Objektpfad.">
              <input type="text" class="hv-leafcfg__input"
                placeholder="h:1234"
                value={draft.address}
                onInput={(e) => set('address', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Scale" help="Roh-Wert × Scale + Offset = kanonischer Wert. Für Analog-Register meist 0.1.">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                placeholder="1.0"
                value={draft.scale}
                onInput={(e) => set('scale', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Offset">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any"
                placeholder="0"
                value={draft.offset}
                onInput={(e) => set('offset', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Poll-Intervall (s)" help="Wie oft der Treiber liest. Leer = Driver-Default.">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step={1} min={0}
                value={draft.pollIntervalSeconds}
                onInput={(e) => set('pollIntervalSeconds', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Heartbeat (s)" help="Publish auch ohne Änderung mind. alle N s. Leer = kein Heartbeat.">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step={1} min={0}
                value={draft.heartbeatSeconds}
                onInput={(e) => set('heartbeatSeconds', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Deadband" help="Änderungen unter diesem Betrag werden unterdrückt (Rauschfilter). Absolut, in kanonischen Einheiten.">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any" min={0}
                value={draft.deadband}
                onInput={(e) => set('deadband', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Deadband (%)"
              help="Relatives Deadband, in Prozent vom letzten gesendeten Wert. Wirkt zusammen mit dem absoluten: es gilt die größere der beiden Schwellen. Leer = keine relative Schwelle.">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step="any" min={0} max={100}
                value={draft.covPercent}
                onInput={(e) => set('covPercent', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
            <Field label="Min. Abstand (s)"
              help="Frühestens alle N Sekunden senden — die Bremse gegen ein oszillierendes Register. Nicht zu verwechseln mit dem Poll-Intervall: das sagt, wie oft gelesen wird.">
              <input type="number" class="hv-leafcfg__input hv-leafcfg__input--num" step={1} min={0}
                value={draft.minIntervalSeconds}
                onInput={(e) => set('minIntervalSeconds', (e.currentTarget as HTMLInputElement).value)} />
            </Field>
          </Section>

          {/*
            Aufzeichnung: bewusst nur Anzeige, kein Schalter. Das Bit gehoert
            dem Telemetrieset, das auf diesem Regler gebunden ist — ein
            Schalter hier waere ein zweiter Besitzer derselben Spalte, und
            beim naechsten Anwenden des Sets waere er still ueberschrieben.
            Der Weg dorthin ist der Bind-Dialog auf der Geraetekarte.
          */}
          <Section title="Aufzeichnung">
            <Field label="Status"
              help="Gesteuert über das Telemetrieset, das auf diesem Regler gebunden ist — nicht hier. Nur aufgezeichnete Datenpunkte landen in der Zeitreihendatenbank und zählen gegen die Lizenzgrenze.">
              <div class="hv-leafcfg__inline-check">
                <StatusDot on={leaf.recordingEnabled} />
                <span class="muted">
                  {leaf.recordingEnabled
                    ? 'wird aufgezeichnet'
                    : 'wird nicht aufgezeichnet'}
                </span>
              </div>
            </Field>
          </Section>

          {error && <div class="hv-leafcfg__error">{error}</div>}
        </div>

        <footer class="hv-leafcfg__footer">
          {onDeleted && (
            confirmDelete ? (
              <>
                <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-muted,#b0b0b0)' }}>
                  Wirklich löschen?
                </span>
                <button
                  type="button"
                  class="hv-btn hv-btn--sm"
                  style={{ background: '#c14040', color: '#fff', borderColor: '#c14040' }}
                  disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await api.delete(`/api/plants/${plantId}/leaves/${leaf.id}`);
                      onDeleted(leaf.id);
                    } catch {
                      setError('Löschen fehlgeschlagen.');
                      setSaving(false);
                    }
                  }}
                >
                  Ja, löschen
                </button>
                <button type="button" class="hv-btn hv-btn--secondary hv-btn--sm" disabled={saving}
                        onClick={() => setConfirmDelete(false)}>
                  Abbrechen
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  class="hv-btn hv-btn--secondary hv-btn--sm"
                  style={{ color: '#f78787', borderColor: 'rgba(220,60,60,0.4)' }}
                  disabled={saving}
                  onClick={() => setConfirmDelete(true)}
                >
                  Löschen
                </button>
                <div style={{ flex: 1 }} />
              </>
            )
          )}
          {!confirmDelete && (
            <>
              <button type="button" class="hv-btn hv-btn--secondary hv-btn--sm" onClick={onClose}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
              <button
                type="button"
                class={`hv-btn hv-btn--primary hv-btn--sm${saving ? ' hv-btn--disabled' : ''}`}
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Speichern…' : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
                )}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: preact.ComponentChildren }) {
  return (
    <section class="hv-leafcfg__section">
      <h3 class="hv-leafcfg__section-title">{title}</h3>
      <div class="hv-leafcfg__section-grid">{children}</div>
    </section>
  );
}

/** Kleiner Zustandspunkt — grün wenn an, gedämpft wenn aus. Reines
 *  Anzeigeelement, deshalb kein Button und kein aria-pressed. */
function StatusDot({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: '50%',
        background: on ? 'var(--ok, #4caf7d)' : 'var(--text-muted, #808080)',
        opacity: on ? 1 : 0.5,
      }}
    />
  );
}

function Field({ label, help, children }: { label: string; help?: string; children: preact.ComponentChildren }) {
  return (
    <div class="hv-leafcfg__field">
      <div class="hv-leafcfg__label">{label}</div>
      {children}
      {help && <div class="hv-leafcfg__help muted">{help}</div>}
    </div>
  );
}

// -- helpers ---------------------------------------------------------------

function initialDraft(leaf: LeafDto): DraftState {
  return {
    displayName: leaf.displayName ?? '',
    unit: leaf.unit ?? '',
    dimension: leaf.dimension ?? '',
    minValue: leaf.minValue == null ? '' : String(leaf.minValue),
    maxValue: leaf.maxValue == null ? '' : String(leaf.maxValue),
    decimals: leaf.decimals == null ? '' : String(leaf.decimals),
    warnLow: leaf.warnLow == null ? '' : String(leaf.warnLow),
    warnHigh: leaf.warnHigh == null ? '' : String(leaf.warnHigh),
    alarmLow: leaf.alarmLow == null ? '' : String(leaf.alarmLow),
    alarmHigh: leaf.alarmHigh == null ? '' : String(leaf.alarmHigh),
    readSensitivity: leaf.readSensitivity ?? 'Normal',
    writeSensitivity: leaf.writeSensitivity ?? 'Normal',
    safetyMarker: leaf.safetyMarker,
    forceAllowed: leaf.forceAllowed,
    escalateToAlarm: leaf.escalateToAlarm,
    metricKind: leaf.metricKind ?? 'Unknown',
    address: leaf.address ?? '',
    scale: leaf.scale == null ? '' : String(leaf.scale),
    offset: leaf.offset == null ? '' : String(leaf.offset),
    pollIntervalSeconds: leaf.pollIntervalSeconds == null ? '' : String(leaf.pollIntervalSeconds),
    heartbeatSeconds: leaf.heartbeatSeconds == null ? '' : String(leaf.heartbeatSeconds),
    deadband: leaf.deadband == null ? '' : String(leaf.deadband),
    covPercent: leaf.covPercent == null ? '' : String(leaf.covPercent),
    minIntervalSeconds: leaf.minIntervalSeconds == null ? '' : String(leaf.minIntervalSeconds),
  };
}

/**
 * Turn the string-backed draft state into a PATCH body. Empty strings on
 * nullable fields go into `clearFields`; non-empty values are parsed. Fields
 * whose value did not change from the original leaf are omitted entirely to
 * keep the request minimal.
 */
function buildRequestBody(draft: DraftState, leaf: LeafDto): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const clear: string[] = [];

  // String fields with clear-vs-set semantics.
  addStringField(body, clear, 'displayName', draft.displayName, leaf.displayName ?? '');
  addStringField(body, clear, 'unit', draft.unit, leaf.unit ?? '');
  addStringField(body, clear, 'dimension', draft.dimension, leaf.dimension ?? '');

  // Nullable numbers.
  addNumericField(body, clear, 'minValue', draft.minValue, leaf.minValue);
  addNumericField(body, clear, 'maxValue', draft.maxValue, leaf.maxValue);
  addIntField(body, clear, 'decimals', draft.decimals, leaf.decimals);
  addNumericField(body, clear, 'warnLow', draft.warnLow, leaf.warnLow);
  addNumericField(body, clear, 'warnHigh', draft.warnHigh, leaf.warnHigh);
  addNumericField(body, clear, 'alarmLow', draft.alarmLow, leaf.alarmLow);
  addNumericField(body, clear, 'alarmHigh', draft.alarmHigh, leaf.alarmHigh);

  // Enum-ish strings (never cleared — always set).
  if (draft.readSensitivity !== leaf.readSensitivity) body.readSensitivity = draft.readSensitivity;
  if (draft.writeSensitivity !== leaf.writeSensitivity) body.writeSensitivity = draft.writeSensitivity;
  if (draft.metricKind !== leaf.metricKind) body.metricKind = draft.metricKind;

  // Booleans.
  if (draft.safetyMarker !== leaf.safetyMarker) body.safetyMarker = draft.safetyMarker;
  if (draft.forceAllowed !== leaf.forceAllowed) body.forceAllowed = draft.forceAllowed;
  if (draft.escalateToAlarm !== leaf.escalateToAlarm) body.escalateToAlarm = draft.escalateToAlarm;

  if (clear.length > 0) body.clearFields = clear;
  return body;
}

function addStringField(body: Record<string, unknown>, clear: string[], key: string, next: string, prev: string): void {
  const trimmedNext = next.trim();
  const trimmedPrev = prev.trim();
  if (trimmedNext === trimmedPrev) return;
  if (trimmedNext === '') clear.push(key);
  else body[key] = trimmedNext;
}

function addNumericField(body: Record<string, unknown>, clear: string[], key: string, next: string, prev: number | null): void {
  const trimmed = next.trim();
  if (trimmed === '') {
    if (prev !== null) clear.push(key);
    return;
  }
  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed)) return; // input still being edited, ignore
  if (parsed === prev) return;
  body[key] = parsed;
}

function addIntField(body: Record<string, unknown>, clear: string[], key: string, next: string, prev: number | null): void {
  const trimmed = next.trim();
  if (trimmed === '') {
    if (prev !== null) clear.push(key);
    return;
  }
  const parsed = parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) return;
  if (parsed === prev) return;
  body[key] = parsed;
}

/**
 * Wire shape returned by PATCH .../leaves/{id}/binding — mirrors the six
 * binding fields we send. Merged into the LeafPatch by the caller so the
 * PlantSchemaPage's local schema stays in sync without a re-fetch.
 */
interface BindingResponse {
  address: string | null;
  scale: number | null;
  offset: number | null;
  pollIntervalSeconds: number | null;
  heartbeatSeconds: number | null;
  deadband: number | null;
  covPercent: number | null;
  minIntervalSeconds: number | null;
}

/**
 * Same shape as buildRequestBody() but for the binding endpoint. Follows
 * the ClearFields pattern the schema endpoint uses: empty string on a
 * previously-set field emits a clearFields entry; non-empty sets the value.
 */
function buildBindingBody(draft: DraftState, leaf: LeafDto): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const clear: string[] = [];

  addStringField(body, clear, 'address', draft.address, leaf.address ?? '');
  // Server field name is PascalCase, ClearFields is compared case-insensitive,
  // but the value fields go over camelCase per Web JSON convention.
  addNumericField(body, clear, 'scale', draft.scale, leaf.scale);
  addNumericField(body, clear, 'offset', draft.offset, leaf.offset);
  addIntField(body, clear, 'pollIntervalSeconds', draft.pollIntervalSeconds, leaf.pollIntervalSeconds);
  addIntField(body, clear, 'heartbeatSeconds', draft.heartbeatSeconds, leaf.heartbeatSeconds);
  addNumericField(body, clear, 'deadband', draft.deadband, leaf.deadband);
  addNumericField(body, clear, 'covPercent', draft.covPercent, leaf.covPercent);
  addIntField(body, clear, 'minIntervalSeconds', draft.minIntervalSeconds, leaf.minIntervalSeconds);

  if (clear.length > 0) body.clearFields = clear;
  return body;
}

/**
 * When only the binding endpoint was called, we still need a LeafPatch
 * with the schema fields for the caller's patcher. Reconstruct one from
 * the leaf's current state — those values didn't change this save.
 */
function synthesizeSchemaPatch(leaf: LeafDto): LeafPatch {
  return {
    leafId: leaf.id,
    displayName: leaf.displayName,
    unit: leaf.unit,
    dimension: leaf.dimension,
    minValue: leaf.minValue,
    maxValue: leaf.maxValue,
    decimals: leaf.decimals,
    warnLow: leaf.warnLow,
    warnHigh: leaf.warnHigh,
    alarmLow: leaf.alarmLow,
    alarmHigh: leaf.alarmHigh,
    readSensitivity: leaf.readSensitivity,
    writeSensitivity: leaf.writeSensitivity,
    safetyMarker: leaf.safetyMarker,
    forceAllowed: leaf.forceAllowed,
    escalateToAlarm: leaf.escalateToAlarm,
    metricKind: leaf.metricKind,
  };
}
